import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';

import DashboardWrap from '../../components/DashboardWrap';
import EmptyPageLoader from '../../components/EmptyPageLoader';
import UserDetailsCardCa from '../../components/UserDetailsCardCa';
import { GET_ADMIN_RENT_PAYMENT_SINGLE } from '../../graphql/admin_queries';
import { cleanSingleRentCollectionRecord } from '../../utils/helpers';
import { LandLordDetailsCard, RentPaymentModal } from './_partials';
import StyledButton from '../../components/StyledButton';
import RolesAndPermissionGuard from '../../components/RolesAndPermissionGuard';
import AdminActionedRecordDetails from '../../components/AdminActionedRecordDetails';
import { AdminRouteName } from '../CONSTANTS';
import GoBackIcon from '../../svgs/GoBackIcon';

import styles from './AdminRentCollectionDetails.module.scss';
import MarkRentAsPaidComp from './_partials/MarkRentAsPaidComp';
import MarkAsPaid from '../../svgs/MarkAsPaid';

const AdminRentCollectionDetails = () => {
  const history = useHistory();
  const [modalDetail, setModalDetail] = useState({
    title: 'Refund Transaction?',
    subTitle: 'Are you sure you want to refund this transaction? This action cannot be undone.',
    action: 'refund',
    actionText: '',
  });
  const [showRentPaymentModal, setShowRentPaymentModal] = useState(false);
  const { rentCollectionId } = useParams<{ rentCollectionId: string }>();
  const [showAdminActionInfo, setShowAdminActionInfo] = useState(true);
  const [showModalChildren, setShowModalChildren] = useState(false);
  const [markAsPaidModal, setMarkAsPaidModal] = useState(false);
  const [isMarkAsPaidOnHover, setIsMarkAsPaidOnHover] = useState(false);

  const { data, refetch, loading } = useQuery<
    {
      getAdminRentPaymentSingle: any;
    },
    { rentPaymentId: string }
  >(GET_ADMIN_RENT_PAYMENT_SINGLE, { variables: { rentPaymentId: rentCollectionId } });

  const rentPayment = useMemo(() => data?.getAdminRentPaymentSingle?.data, [data]);
  const payload = cleanSingleRentCollectionRecord(rentPayment);

  const handleToggleModal = (type: string) => {
    setShowRentPaymentModal(true);
    const typeTitles = {
      settle: 'Settle Transaction?',
      refund: 'Refund Transaction?',
      generateEquifaxFile: 'Generate Equifax File',
      markEquifaxFileAsSubmitted: 'Mark as Submitted to Equifax',
    };
    const typeSubTitles = {
      settle: `You are about to settle ${payload?.name}'s Rent to their Landlord`,
      refund: 'Are you sure you want to refund this transaction? This action cannot be undone.',
      generateEquifaxFile: 'You are about to generate an Equifax file for this transaction',
      markEquifaxFileAsSubmitted: 'You are about to mark this rent payment as submitted to Equifax',
    };

    const actionText = {
      settle: type,
      refund: type,
      generateEquifaxFile: 'Generate File',
      markEquifaxFileAsSubmitted: 'Continue',
    };

    setModalDetail({
      title: typeTitles[type],
      subTitle: typeSubTitles[type],
      action: type,
      actionText: actionText[type],
    });
    if (type === 'settle') {
      setShowModalChildren(true);
    }
  };

  const stat = ['settled', 'refunded'];

  const disableBtn = stat?.includes(payload?.settlementStatus);
  const trxIsPending = payload?.status === 'PENDING';

  const activeRentReporting = payload?.rentReportStatus === 'INTERESTED';

  const showGenerateEquifaxFile = payload?.settlementStatus === 'settled' && !payload?.equifaxSubmitted;

  const showMarkAsSubmittedToEquifax = payload?.settlementStatus === 'settled' && !payload?.equifaxSubmitted && payload?.equifaxGenerated;

  return (
    <DashboardWrap className={styles.rentCollectionDetailsLayout}>
      {
        loading ? <EmptyPageLoader /> : (
          <div className={styles.wrapper}>
            <div className={styles.wrapper__top}>
              <div className={styles.wrapper__titleContainer}>
                <GoBackIcon onClick={() => history?.goBack()} />
                Rent collection
              </div>
              <UserDetailsCardCa payload={payload}>
                {trxIsPending ? (
                  <RolesAndPermissionGuard permissionName="can-process-manual-rent-payment-transaction">
                    <StyledButton onClick={() => setMarkAsPaidModal(true)} onMouseEnter={() => setIsMarkAsPaidOnHover(true)} onMouseLeave={() => setIsMarkAsPaidOnHover(false)} gap="12px" icon={<MarkAsPaid style={isMarkAsPaidOnHover ? { fill: '#00C48C', path: '#00C48C' } : ''} />} name="Mark as Paid" color={isMarkAsPaidOnHover ? '#523B9F' : '#667085'} border="1px solid #5941A955" fontSize="14px" height="40px" backgroundColor={isMarkAsPaidOnHover ? 'rgba(220, 216, 236, 0.7)' : '#fff'} />
                  </RolesAndPermissionGuard>
                ) : (
                  <div className={styles.wrapper__btnWrapper}>
                    {
                      showGenerateEquifaxFile && (
                        <RolesAndPermissionGuard permissionName="can-update-rent-payment-settlement-status">
                          <StyledButton
                            name="Get Equifax File"
                            onClick={() => handleToggleModal('generateEquifaxFile')}
                            backgroundColor="#5941A9"
                            color="#fff"
                            border="none"
                            // width="97px"
                            height="40px"
                            fontSize="14px"
                            fontWeight={900}
                            borderRadius="4px"
                          />
                        </RolesAndPermissionGuard>
                      )
                    }
                    {
                      showMarkAsSubmittedToEquifax && (
                      <RolesAndPermissionGuard permissionName="can-update-rent-payment-settlement-status">
                        <StyledButton
                          name="Mark as submitted to Equifax"
                          onClick={() => handleToggleModal('markEquifaxFileAsSubmitted')}
                          backgroundColor="white"
                          color="#667085"
                          border="1px solid #667085"
                          // width="97px"
                          height="40px"
                          fontSize="14px"
                          fontWeight={900}
                          borderRadius="4px"
                        />
                      </RolesAndPermissionGuard>
                      )
                    }
                    {
                      payload?.settlementStatus === 'refunded' && (
                      <StyledButton
                        name="REFUNDED"
                        backgroundColor="white"
                        color="#667085"
                        border="1px solid #667085"
                        width="97px"
                        height="40px"
                        fontWeight={900}
                        fontSize="14px"
                        borderRadius="4px"
                        disabled
                      />
                      )
                    }
                    {(payload?.settlementStatus === 'settled' || payload?.settlementStatus !== 'refunded') && !showMarkAsSubmittedToEquifax && !showGenerateEquifaxFile && (
                    <RolesAndPermissionGuard permissionName="can-update-rent-payment-settlement-status">
                      <StyledButton
                        name={`${payload?.settlementStatus === 'settled' ? 'SETTLED' : 'SETTLE'}`}
                        onClick={() => handleToggleModal('settle')}
                        backgroundColor={`${disableBtn ? '#E2E8F0' : '#5941A9'}`}
                        color="#fff"
                        border={`${disableBtn ? 'none' : '1px solid #EAECF0'}`}
                        width="97px"
                        height="40px"
                        fontSize="14px"
                        fontWeight={900}
                        borderRadius="4px"
                        disabled={disableBtn}
                      />
                    </RolesAndPermissionGuard>
                    )}
                    {(payload?.settlementStatus === 'refunded' || payload?.settlementStatus !== 'settled') && (
                    <RolesAndPermissionGuard permissionName="can-update-rent-payment-settlement-status">
                      <StyledButton
                        name={`${payload?.settlementStatus === 'refunded' ? 'REFUNDED' : 'REFUND'}`}
                        onClick={() => handleToggleModal('refund')}
                        backgroundColor={`${disableBtn ? '#E2E8F0' : 'white'}`}
                        color={`${disableBtn ? 'white' : '#667085'}`}
                        border={`${disableBtn ? 'none' : '1px solid #667085'}`}
                        width="97px"
                        height="40px"
                        fontWeight={900}
                        fontSize="14px"
                        borderRadius="4px"
                        disabled={disableBtn}
                      />
                    </RolesAndPermissionGuard>

                    )}
                  </div>
                )}
              </UserDetailsCardCa>
              <LandLordDetailsCard
                payload={payload}
                activeRentReportingStatus={activeRentReporting}
              />
            </div>
            {(showAdminActionInfo && payload?.actionTakenBy) && (
            <div className={styles.wrapper__actionContainer}>
              <AdminActionedRecordDetails
                recordName={`Rent Collection ${payload?.settlementStatus === 'refunded' ? 'Refund' : 'Settlement'}!`}
                action={`${payload?.settlementStatus === 'refunded' ? 'refunded' : 'settled'}`}
                lastName={payload?.actionTakenBy?.lastName || '*******'}
                firstName={payload?.actionTakenBy?.firstName || '*******'}
                recordTitle="rent payment"
                routePath={AdminRouteName?.rentCollectionRoute}
                tableTitle="Rent Collection"
                onClose={() => setShowAdminActionInfo(false)}
              />
            </div>
            )}
            {showRentPaymentModal && (
            <RentPaymentModal
              id={payload?.id}
              payload={payload}
              refetch={refetch}
              modalDetail={modalDetail}
              isShow={showRentPaymentModal}
              setModalDetail={setModalDetail}
              setShowRentPaymentModal={setShowRentPaymentModal}
              showModalChildren={showModalChildren}
              setShowModalChildren={setShowModalChildren}
            />
            )}
          </div>
        )
      }
      {
        markAsPaidModal && (
        <MarkRentAsPaidComp
          closeModal={() => setMarkAsPaidModal(false)}
          docName={`${payload?.user?.firstName}'s Rent Payment`}
          rentPaymentId={payload?.id}
          refetch={refetch}
          permissionName="can-process-manual-rent-payment-transaction"
        />
        )
      }
    </DashboardWrap>
  );
};

export default AdminRentCollectionDetails;
